/* eslint-disable sonarjs/no-identical-functions */
import {
  Analytics,
  GTMEventTypes,
  GTMVirtualPageViewPayload,
  GTMEventCategory,
  EventValues,
} from '@sial/common-utils'

import {
  isAnalyticsEnabled,
  sendEvent,
  getCurrencyCode,
  regexForStripEncodingUnicode,
  regexForStripHTML,
  regexForStripSymbols,
  regexForStripHTMLWithContent,
  DEFAULT_CURRENCY_CODE,
  CART_DATA_STORAGE_KEY,
  clubbingUpProducts,
  priceFormatter,
} from '@utils/analytics/coreAnalytics'

import {
  GTMEcommerceValues,
  SearchAnalyticsEventParams,
  RecommendedProduct,
  GTMVirtualPageViewPayloadWithPersistentLogin,
  GA4SearchEventPayload,
  SharedListsEvent,
  GA4SharedListsEventPayload,
  GA4ListInteractionEventPayload,
  GA4PDPClickPayload,
  DownloadFileEvent,
  Menu,
  GA4ProductDetailsEventPayload,
  GA4QuickOrderTrackingPayload,
  ProductAnalytics,
  GA4DownloadFilePayload,
  EventActions,
  GA4ViewSearchResultsPayload,
  GA4CommonDetailClickEvent,
  ViewSampleCOAEvent,
  GA4CommonDetailProductIdEvent,
  GA4SharedListEventPayload,
  GA4CSRFiltersEvent,
  GA4ErrorEvent,
  GA4SearchInteractionPayload,
  GA4QuickOrderInteractionPayload,
  ProductDetails,
  GA4EmptyRecommendationPayload,
  shoppingAiInteractionPayload,
  ProductDetailPromoPayload,
  NewSrpProductAnalytics,
} from '@utils/analytics/types'

import {
  EventKeys,
  SearchPageEnum,
  AdvancedSearchTypeEnum,
  ProductDetailsEventAction,
  AddToCartPagesEnum,
  DocumentLibraryTab,
  EventType,
} from '@utils/analytics/enums'

import {
  CartItemFragment,
  CartTotalsFragment,
} from '@src/queries/CartQuery.generated'

import { DidYouMeanTerm } from '@src/types/graphql-types'

import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { OrderDetailsFragment } from '@src/queries/OrderDetailsQuery.generated'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { useCookies } from '@src/utils/cookies'
import messages, { Message } from '@utils/messages'
import {
  ProductSearchType,
  SearchFocusType,
  GeneSearchType,
} from '@utils/searchUtils'
import decodeTitle from '@utils/decodeTitle'
import { GuestShoppingType, useCurrentUser } from '@utils/useCurrentUser'
import { pickBy, isArray } from 'lodash'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import React, { useEffect, useLayoutEffect } from 'react'
import { sessionStorage } from 'react-storage'
import {
  getPageTealiumSetting,
  sendUtagView,
  sendOrderConfirmationB2BTrackForAnalytic,
  productDetailPageViewTrack,
  sendSuccessfulLoginEventForTealium,
} from './tealiumAnalytics'
import loginStorage from '@src/utils/loginStorage'
import { Url } from 'next/dist/shared/lib/router/router'
import { ProductCardProps } from '@src/routes/ShoppingAi/ShoppingAiForm/Messages/ProductCard/ProductCard'

const {
  publicRuntimeConfig: {
    uTagEnv,
    gtmTagId,
    isTealiumEnabled,
    isGoogleTagManagerEnabled,
  },
} = getConfig()

export const LAST_REFERRER_URL = 'LAST_REFERRER_URL'
export const CURRENT_PAGE_URL = 'CURRENT_PAGE_URL'

export const PAGEVIEW_TIMEOUT = 2000
export const ONLOAD_EVENT_TIMEOUT = PAGEVIEW_TIMEOUT + 1000
export const DOCUMENT_REFERRER = 'DOCUMENT_REFERRER'
const PAGE_REFRESH = 'PAGE_REFRESH'

export { uTagEnv, gtmTagId }

const PAGE_VIEW_SENT = 'PAGE_VIEW_SENT'

/**
 *  GTMEvent component
 *
 *  @param props - GTMEventProps
 *
 *  This is the basic Google Tag Manager component for sending custom events.
 *  They payload is sent as a prop and is integrated with the event type.
 *  For the AnalyticsEvent type of event, you would use the following:
 *
 *    import { GTMEvent, GTMEventTypes } from '@src/utils/analytics'
 *
 *    <GTMEvent
 *      eventType={GTMEventTypes.AnalyticsEvent} // enum value is 'analyticsEvent'
 *      payload={{
 *        eventCategory:'TEST CATEGORY',
 *        eventAction:'TEST ACTION',
 *        eventLabel:'TEST LABEL',
 *      }}
 *    />
 *
 *  This translates (roughly) to the following:
 *
 *    <script type="text/javascript">
 *      dataLayer = dataLayer || [];
 *      dataLayer.push({
 *        'event':'analyticsEvent', //hardcoded
 *        'eventCategory':'{{event category}}',
 *        'eventAction':'{{event action}}',
 *        'eventLabel':'{{event label}}',
 *        'eventInteractionType':0
 *      });
 *    </script>
 *
 * @param legacyUAFields
 **/

export const sendNavigationInteractionEvent = ({
  action,
  detail,
  section,
  component,
  elementType,
  elementText,
  linkUrl,
  productId,
  productBrand,
  productVariant,
  productName,
}: {
  action: string
  detail?: string | null
  section?: string | undefined
  component: string
  elementType: string
  elementText?: string | Maybe<string>
  linkUrl?: Url | Maybe<string>
  productId?: string | undefined
  productBrand?: string | undefined
  productVariant?: string | undefined
  productName?: string | undefined
}): void => {
  const basePayload = {
    event: EventType.Navigation_Interaction,
    action,
    detail:
      detail?.toLowerCase().replace(regexForStripHTMLWithContent, '') ||
      undefined,
    section,
    component,
    element_type: elementType,
    element_text:
      elementText?.toLowerCase().replace(regexForStripHTMLWithContent, '') ||
      undefined,
    link_url: linkUrl || undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId?.toLowerCase() || undefined,
    product_name: productName
      ?.replace(regexForStripHTML, '')
      .replace(regexForStripSymbols, '')
      .toLowerCase(),
    product_brand: productBrand?.toLowerCase() || undefined,
    product_variant: productVariant?.toLowerCase() || undefined,
  }
  sendEvent({ payload: basePayload })
}

const getMetaKeywords = (): string => {
  if (typeof document === 'undefined') return EventValues.Empty

  const element = document?.querySelector('meta[name="keywords"]')
  const content = element && element?.getAttribute('content')
  return content
    ? content?.length
      ? content?.split(',').join(' |')
      : EventValues.NotAvailable
    : EventValues.NotAvailable
}

export const sendLoginEvent = ({ me }): void => {
  if (!isGoogleTagManagerEnabled) {
    return
  }
  if (me?.oidcSignInSuccess) {
    const [method, idp] = me?.oidcSignInSuccess.split('|')
    sendEvent({
      eventType: GTMEventTypes.AnalyticsEvent,
      payload: {
        event: GTMEventCategory.Login.toLowerCase(),
        login_participant_id: me?.id,
        login_reauthentication: '',
        login_type: idp ? idp : 'non-persistent login',
        method: method,
      },
    })
  } else {
    const loginType =
      me?.profileType === 'B2B'
        ? 'b2b login'
        : loginStorage.get()?.rememberMe
          ? 'persistent login'
          : 'non-persistent login'

    //GA4 login
    sendEvent({
      eventType: GTMEventTypes.AnalyticsEvent,
      payload: {
        event: GTMEventCategory.Login.toLowerCase(),
        login_participant_id: me?.id,
        login_reauthentication: '',
        login_type: loginType,
        method: 'password',
      },
    })
  }
}

export const isHomePage = (routeAsPath: string): boolean =>
  (routeAsPath || '')?.split('/')?.length === 3

export const hasURLSearchParams = (): number => window.location.search?.length

export const sendScrollTrackingEvent = (scrolPercentage?: string): void => {
  //GA4
  const scrollPercentageRemoved = scrolPercentage?.slice(
    0,
    scrolPercentage.length - 1
  )
  sendEvent({
    payload: {
      event: 'scroll',
      percent_scrolled: scrollPercentageRemoved,
    },
  })
}

const PAGE_LOAD = 'PAGE_LOAD'

export const onLoadScrollEvent = (): void => {
  const visibleScreenPercentage =
    (window.innerHeight * 100) / document.body.scrollHeight

  if (visibleScreenPercentage >= 100) {
    sendScrollTrackingEvent('100%')
  } else if (visibleScreenPercentage >= 75) {
    sendScrollTrackingEvent('75%')
  } else if (visibleScreenPercentage >= 50) {
    sendScrollTrackingEvent('50%')
  } else if (visibleScreenPercentage >= 25) {
    sendScrollTrackingEvent('25%')
  }
}

export const setPageLoadComplete = (isLoad: boolean): void => {
  onLoadScrollEvent()
  return sessionStorage.setItem(PAGE_LOAD, isLoad)
}

const isValidPath = (searchTerm?: string): boolean => {
  const currentPagePath = (window.location.pathname || '')
    .split('/')
    .slice(3)
    .join('/')
  const paths = [
    `search/${searchTerm}`,
    'search/structure-search',
    'product/compare',
  ]
  return paths.includes(currentPagePath) ? false : true
}

const pageLoad = (searchTerm?: string): void => {
  const isLoad = isValidPath(searchTerm)
  return sessionStorage.setItem(PAGE_LOAD, isLoad)
}

export const isPageLoad = (): string | boolean =>
  sessionStorage.getItem(PAGE_LOAD) || false

const AnalyticsScrollTracking = () => {
  const router = useRouter()

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const twentyFivePercentListener = (): void => {
      const visibleScreenPercentage =
        (window.innerHeight * 100) / document.body.scrollHeight
      const currentRatio =
        (window.pageYOffset /
          (document.body.scrollHeight - window.innerHeight)) *
        100
      if (isPageLoad() && currentRatio >= 25 && visibleScreenPercentage <= 25) {
        document.removeEventListener('scroll', twentyFivePercentListener)
        sendScrollTrackingEvent('25%')
      }
    }
    document.addEventListener('scroll', twentyFivePercentListener)

    const fiftyPercentListener = (): void => {
      const visibleScreenPercentage =
        (window.innerHeight * 100) / document.body.scrollHeight
      const currentRatio =
        (window.pageYOffset /
          (document.body.scrollHeight - window.innerHeight)) *
        100
      if (isPageLoad() && currentRatio >= 50 && visibleScreenPercentage <= 50) {
        document.removeEventListener('scroll', fiftyPercentListener)
        sendScrollTrackingEvent('50%')
      }
    }
    document.addEventListener('scroll', fiftyPercentListener)

    const seventyFivePercentListener = (): void => {
      const visibleScreenPercentage =
        (window.innerHeight * 100) / document.body.scrollHeight
      const currentRatio =
        (window.pageYOffset /
          (document.body.scrollHeight - window.innerHeight)) *
        100
      if (isPageLoad() && currentRatio >= 75 && visibleScreenPercentage <= 75) {
        document.removeEventListener('scroll', seventyFivePercentListener)
        sendScrollTrackingEvent('75%')
      }
    }
    document.addEventListener('scroll', seventyFivePercentListener)

    const hunderedPercentListener = (): void => {
      const visibleScreenPercentage =
        (window.innerHeight * 100) / document.body.scrollHeight
      const currentRatio =
        (window.pageYOffset /
          (document.body.scrollHeight - window.innerHeight)) *
        100
      if (
        isPageLoad() &&
        currentRatio >= 99.9 &&
        visibleScreenPercentage <= 100
      ) {
        document.removeEventListener('scroll', hunderedPercentListener)
        sendScrollTrackingEvent('100%')
      }
    }
    document.addEventListener('scroll', hunderedPercentListener)

    // Initial sending the event how much screen visible without scrolling
    if (isValidPath(router?.query?.searchTerm || '')) {
      onLoadScrollEvent()
    }
    return () => {
      document.removeEventListener('scroll', twentyFivePercentListener)
      document.removeEventListener('scroll', fiftyPercentListener)
      document.removeEventListener('scroll', seventyFivePercentListener)
      document.removeEventListener('scroll', hunderedPercentListener)
    }
  }, [router.asPath])
  return <></>
}

export const setLastReferrerUrl = (): void =>
  sessionStorage.setItem(LAST_REFERRER_URL, window.location.href)

/**
 *  GTMAnalyticsPageView component
 *
 *  This records a virtual page view for the visited page when GTM is enabled.
 */
const GTMAnalyticsPageView = () => {
  const router = useRouter()
  const {
    userIsLoggedIn,
    memberId,
    userId,
    roleNames,
    emproveUserType,
    currentUserLoading,
    isB2BUser,
    currentUser,
    isDealer,
    isDTAQFTBUser,
    isPublicOrderingCustomer,
    guestShoppingType,
    isDTAQZuCustomer,
    primaryBusinessActivity,
    organizationType,
    organizationWebsite,
    organizationPosition,
  } = useCurrentUser()

  const [cookies] = useCookies([
    'GUID',
    'language',
    'country',
    '_ga',
    'InternalUser',
  ])

  const primaryBusinessActivityString =
    primaryBusinessActivity &&
    Object.keys(
      pickBy(primaryBusinessActivity, (value) => value === true)
    ).join(' | ')

  const soldTos =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.soldToPartners.map((partner) => partner.soldTo).join(' | ')

  const shipTos =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.soldToPartners.map((partner) => partner.shipTo).join(' | ')

  const billTos =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.soldToPartners.map((partner) => partner.billTo).join(' | ')

  const participantId = (userIsLoggedIn && userId) || ''
  sessionStorage.setItem('PARTICIPANT_ID', participantId)

  const redirectReferrer: string =
    sessionStorage.getItem(DOCUMENT_REFERRER) || ''

  const directCustomerProcurement = isB2BUser
  const directCustomerEShop =
    !isB2BUser && isPublicOrderingCustomer && !isDealer
  const dealerCartEndCustomer =
    !isB2BUser &&
    guestShoppingType === GuestShoppingType.DTAQ &&
    isDTAQZuCustomer

  useLayoutEffect(() => {
    sessionStorage.setItem(PAGE_REFRESH, true)
  }, [])

  useLayoutEffect(() => {
    /**
     function to return payload for virtualPageview event
     **/
    const isPageRefreshed = sessionStorage.getItem(PAGE_REFRESH)
    const pathnameAndSearch = window.location.pathname + window.location.search
    const getVirtualPageviewPayload =
      (): GTMVirtualPageViewPayloadWithPersistentLogin => {
        const payload: GTMVirtualPageViewPayload = {
          VirtualPagePath: pathnameAndSearch,
          VirtualPageTitle: document.title,
          VirtualPageParameters: window.location.search,
          VirtualPageURL: window.location.href,
          Language: cookies.language,
          Country: cookies.country,
          OriginalReferrer: isPageRefreshed
            ? redirectReferrer ||
              sessionStorage.getItem(LAST_REFERRER_URL) ||
              ''
            : sessionStorage.getItem(LAST_REFERRER_URL) ||
              redirectReferrer ||
              '',
          GUID: cookies?.GUID?.split('|')[0],
          LoggedIn: userIsLoggedIn ? EventValues.Yes : EventValues.No,
          InternalUser: cookies?.internalUser,
          ContentPageType: getMetaKeywords(),
          ProductPageCategory: '', // TBD - only for PDP's
          persistentLogin: userId
            ? isB2BUser
              ? 'b2b'
              : loginStorage.get()?.rememberMe
                ? 'yes'
                : 'no'
            : 'not logged in',
          ...(userIsLoggedIn
            ? {
                ParticipantId: userId,
                MemberId: memberId || EventValues.NotAvailable,
                SoldToNumber: soldTos || EventValues.NotAvailable,
                BillToNumber: billTos || EventValues.NotAvailable,
                ShipToNumber: shipTos || EventValues.NotAvailable,
                Roles:
                  roleNames?.replace(/-/g, '|') || EventValues.NotAvailable,
                EmproveUserType: emproveUserType,
                OrganizationType: organizationType || EventValues.NotAvailable,
                OrganizationPosition:
                  organizationPosition || EventValues.NotAvailable,
                OrganizationWebsite:
                  organizationWebsite || EventValues.NotAvailable,
                PrimaryBusinessActivity:
                  primaryBusinessActivityString || EventValues.NotAvailable,
                UserType: isB2BUser ? 'B2B' : 'B2C',
                ...(isPageRefreshed
                  ? {
                      JapanUserType: directCustomerProcurement
                        ? 'Direct Customer Procurement'
                        : isDTAQFTBUser
                          ? 'FTB'
                          : directCustomerEShop
                            ? 'Direct Customer eShop'
                            : dealerCartEndCustomer
                              ? 'Dealer Cart End Customer'
                              : isDealer
                                ? 'Dealer'
                                : EventValues.NotAvailable,
                    }
                  : {}),
              }
            : {}),
        } as GTMVirtualPageViewPayloadWithPersistentLogin
        return payload
      }

    sessionStorage.setItem(PAGE_VIEW_SENT, false)
    pageLoad(router?.query?.searchTerm || '')
    const timeout = setTimeout(() => {
      !isPageRefreshed &&
        Analytics.virtualGTMPageView({
          ...getVirtualPageviewPayload(),
        } as GTMVirtualPageViewPayloadWithPersistentLogin)

      if (
        isB2BUser &&
        !sessionStorage.getItem<boolean>(EventKeys.IS_B2B_LOGGED_IN_CALLED)
      ) {
        sendLoginEvent({
          me: currentUser,
        })
        sessionStorage.setItem<boolean>(EventKeys.IS_B2B_LOGGED_IN_CALLED, true)
      }
      if (
        isPageRefreshed &&
        isAnalyticsEnabled &&
        isGoogleTagManagerEnabled &&
        typeof window !== 'undefined' &&
        !(window.ga && window.ga.create)
      ) {
        Analytics.TagManager.dataLayer({
          dataLayer: {
            ...getVirtualPageviewPayload(),
          },
        })
        Analytics.initializeGoogleTagManager(gtmTagId)
      }
      sessionStorage.setItem(PAGE_VIEW_SENT, true)
      sessionStorage.setItem(CURRENT_PAGE_URL, window.location.href)
      Analytics.dequeueEvents()
      setLastReferrerUrl()
      sessionStorage.setItem(PAGE_REFRESH, false)
      // HACK: 2 seconds is the amount of time we can reasonably assume a page is fully loaded to simulate a DOM Ready event.
    }, PAGEVIEW_TIMEOUT)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [currentUserLoading, router.asPath])

  useEffect(() => {
    // Client-side routing doesn't change the referrer and we can't set the referer manually
    // so we need some kind of storage solution to track the value.
    const handleSessionStorage = (url: string): void => {
      //no need to set referrer if the path is same.
      if (router.asPath !== url.split('?')[0]) {
        const currentPath =
          sessionStorage.getItem<string>(CURRENT_PAGE_URL) || EventValues.Empty
        sessionStorage.setItem<string>(LAST_REFERRER_URL, currentPath)
      }
    }

    router.events.on('routeChangeStart', handleSessionStorage)

    // If the component is unmounted, unsubscribe from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleSessionStorage)
    }
  }, [router.events])

  return <AnalyticsScrollTracking />
}

/**
 * virtualTealiumPageView function
 *
 * @param url - currently this takes window.location.pathname, though could be window.location.href (full url)
 */
export function virtualTealiumPageView(
  path: string,
  retry: boolean,
  userIsLoggedIn: boolean,
  userErpType?: string
): void {
  if (isAnalyticsEnabled) {
    if (typeof window !== 'undefined' && window.utag && window.utag.view) {
      const { tealiumEvent, siteSection, skipGlobalAnalytic } =
        getPageTealiumSetting(path)
      if (!skipGlobalAnalytic) {
        sendUtagView({ tealiumEvent, siteSection, userIsLoggedIn, userErpType })
      }
    } else if (retry) {
      throw new Error("utag hasn't been loaded yet.")
    }
  }
}

/**
 * TealiumAnalyticsPageView component
 *
 * This fires a virtual page view to Tealium (China-only) whenever the given page is visited.
 */
export const TealiumAnalyticsPageView = () => {
  const router = useRouter()
  const {
    currentUserLoading,
    userIsLoggedIn,
    roleNames,
    currentUser,
    emproveUserType,
    isB2BUser,
    userId,
    primaryBusinessActivity,
    organizationType,
    organizationWebsite,
    organizationPosition,
    userErpType,
  } = useCurrentUser()

  const [cookies] = useCookies(['_ga'])
  const participantId = (userIsLoggedIn && userId) || ''
  sessionStorage.setItem('PARTICIPANT_ID', participantId)
  const isOffersPromotionsEmail =
    currentUser?.__typename === 'LoggedInUser'
      ? currentUser?.participantCommunicationSetting?.isOffersPromotionsEmail
      : ''
  pageLoad(router?.query?.searchTerm || '')
  useEffect(() => {
    // Client-side routing doesn't change the referrer and we can't set the referer manually
    // so we need some kind of storage solution to track the value.
    const handleWindowLocation = (): void => {
      sessionStorage.setItem(LAST_REFERRER_URL, window.location.href)
    }

    router.events.on('routeChangeStart', handleWindowLocation)

    // If the component is unmounted, unsubscribe from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleWindowLocation)
    }
  }, [router.events])

  useLayoutEffect(() => {
    let timeout
    try {
      virtualTealiumPageView(router.asPath, true, userIsLoggedIn, userErpType)
    } catch (e) {
      timeout = setTimeout(() => {
        virtualTealiumPageView(
          router.asPath,
          false,
          userIsLoggedIn,
          userErpType
        )
      }, 2000)
      // Hack: Introduce a retry mechnism to cover sometimes the utag has not loaded for the first page
      // it will have a retry in 2s later
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [router.asPath])

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      if (
        isB2BUser &&
        !sessionStorage.getItem<boolean>(EventKeys.IS_B2B_LOGGED_IN_CALLED)
      ) {
        sendSuccessfulLoginEventForTealium({
          me: currentUser,
          gaId: `b2b-login > ${cookies._ga}`,
          isOffersPromotionsEmail,
          roleNames,
          currentUser,
          emproveUserType,
          organizationType,
          organizationPosition,
          organizationWebsite,
          primaryBusinessActivity,
          isB2BUser,
        })
        sessionStorage.setItem<boolean>(EventKeys.IS_B2B_LOGGED_IN_CALLED, true)
      }
      // track wechat login
      if (sessionStorage.getItem<boolean>(EventKeys.IS_WECHAT_LOGGED_IN)) {
        sendSuccessfulLoginEventForTealium({
          me: currentUser,
          gaId: `wechat-login > ${cookies._ga}`,
          isOffersPromotionsEmail,
          roleNames,
          currentUser,
          emproveUserType,
          organizationType,
          organizationPosition,
          organizationWebsite,
          primaryBusinessActivity,
          isB2BUser,
        })
        sessionStorage.setItem<boolean>(EventKeys.IS_WECHAT_LOGGED_IN, false)
      }
    }, 2000)

    return () => {
      clearTimeout(timeout)
    }
  }, [currentUserLoading, router.asPath])
  return <AnalyticsScrollTracking />
}

/**
 *  AnalyticsPageView component
 *
 *  This fires a virtual page view whenever the given page is visited.
 */
export const AnalyticsPageView = () => {
  // If not client side or analytics are entirely disabled don't load any page view scripts.
  if (
    !isAnalyticsEnabled ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return null
  }

  return (
    <>
      {isGoogleTagManagerEnabled && <GTMAnalyticsPageView />}
      {isTealiumEnabled && <TealiumAnalyticsPageView />}
    </>
  )
}

/**
 * sendSearchAnalyticsEvent
 *
 * This sends an event to Google for search-related events.
 */
export const sendSearchAnalyticsEvent = (
  { searchType, item, searchTerm, facetValue }: SearchAnalyticsEventParams,
  focusType?: string
): void => {
  let autoSuggestType: string
  let autoSuggestTerm: string
  switch (searchType) {
    case ProductSearchType.ProductName:
      autoSuggestType = EventValues.ProductName.toLowerCase()
      autoSuggestTerm = item || EventValues.Empty
      break
    case ProductSearchType.ProductNumber:
      autoSuggestType = EventValues.ProductNumber.toLowerCase()
      autoSuggestTerm = item || EventValues.Empty
      break
    case ProductSearchType.Product:
      // For Categories type auto suggestion adding prefix 'in' between search term and auto suggest item

      autoSuggestType = EventValues.Categories.toLowerCase()
      autoSuggestTerm = `${item} in ${
        facetValue?.toLowerCase() || EventValues.Empty
      }`
      break
    case ProductSearchType.CasNumber:
      autoSuggestType = EventValues.CASNumber.toLowerCase()
      autoSuggestTerm = item || EventValues.Empty
      break
    case ProductSearchType.RecentPurchases:
      autoSuggestType = 'recent purchases'
      autoSuggestTerm = item || EventValues.Empty
      break
    default:
      autoSuggestType = EventValues.Empty
      autoSuggestTerm = EventValues.Empty
  }

  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search',
      search_box_input: searchTerm || 'no_user_input',
      search_term: item?.toLowerCase() || undefined,
      search_auto_suggest: autoSuggestType,
      search_auto_suggest_term: autoSuggestTerm?.toLowerCase(),
      search_type: focusType,
      search_component: 'global search',
      search_error_message: undefined,
    },
  })
}

export const sendHeaderSearchEvent = (
  ga4payload?: GA4SearchEventPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search',
      search_term: ga4payload?.searchTerm || 'no_user_input',
      search_auto_suggest: ga4payload?.searchAutoSuggest,
      search_auto_suggest_term:
        ga4payload?.searchAutoSuggestTerm?.toLowerCase(),
      search_type: ga4payload?.searchType,
      search_component: ga4payload?.searchComponent,
      search_error_message: ga4payload?.searchErrorMessage,
    },
  })
}

export const sendSharedListEvent = (
  ga4Payload: GA4SharedListEventPayload
): void => {
  const basePayload = {
    event: ga4Payload?.event || 'list_interaction',
    action: ga4Payload?.action,
    detail: ga4Payload?.detail,
    section: ga4Payload?.section,
    component: ga4Payload?.component,
    element_type: ga4Payload?.elementType,
    element_text: ga4Payload?.elementText?.toLowerCase(),
    link_url: ga4Payload?.linkUrl,
    core_event: ga4Payload?.coreEvent,
    user_detail: ga4Payload?.userDetail,
    event_group: ga4Payload?.eventGroup,
    event_subgroup: ga4Payload?.eventSubgroup,
    product_id: ga4Payload?.productId?.toLowerCase() || undefined,
    product_brand: ga4Payload?.productBrand?.toLowerCase() || undefined,
    product_variant: ga4Payload?.productVariant?.toLowerCase() || undefined,
    product_name:
      ga4Payload?.productName
        ?.replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .replace(regexForStripEncodingUnicode, '')
        .toLowerCase() || undefined,
  }

  sendEvent({ payload: basePayload })
}

// leaving this until the rest of the list events
// are updated to the new GA4 data structure
export const sendSharedListsEvent = (
  event: SharedListsEvent,
  ga4payload?: GA4SharedListsEventPayload
): void => {
  // GA4 Event Send
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'list_clicks',
      click_type: ga4payload?.clickType,
      click_action: ga4payload?.clickAction,
      click_details: isArray(ga4payload?.clickDetails)
        ? ga4payload?.clickDetails.join(' > ')
        : ga4payload?.clickDetails || undefined,
      component: ga4payload?.component,
      element_type: ga4payload?.elementType,
      link_text: ga4payload?.linkText,
      link_url: ga4payload?.linkUrl || undefined,
    },
  })
}

// New GA4 event for list interaction
export const sendListInteractionEvent = (
  ga4payload: GA4ListInteractionEventPayload
): void => {
  const basePayload = {
    event: 'list_interaction',
    action: ga4payload?.action,
    detail: undefined,
    section: 'recently updated list',
    component: ga4payload?.component,
    element_type: ga4payload?.elementType,
    element_text: ga4payload?.elementText,
    link_url: ga4payload?.linkUrl || undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: undefined,
    product_brand: undefined,
    product_variant: undefined,
    product_name: undefined,
  }

  sendEvent({
    payload: basePayload,
  })
}

export const sendSharedListQuoteRequestEvent = (
  payload: GA4SharedListEventPayload
): void => {
  sendEvent({
    payload: {
      event: 'complete_quote_request',
      action: payload?.action || undefined,
      detail: payload?.detail,
      section: payload?.section,
      component: payload?.component,
      element_type: payload?.elementType,
      element_text: payload?.elementText,
      link_url: payload?.linkUrl || undefined,
      core_event: payload?.coreEvent || 'no',
      user_detail: payload?.userDetail || undefined,
      event_group: payload?.eventGroup || undefined,
      event_subgroup: payload?.eventSubgroup || undefined,
    },
  })
}

export const sendLoginErrorEvent = ({ message }): void =>
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'exception',
      description: message.toLowerCase() || undefined,
      error_category: 'login errors',
      product_id: undefined,
      product_name: undefined,
      product_brand: undefined,
    },
  })

export const sendCartUserIdLockedEvent = ({ message }): void =>
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'exception',
      description: message.toLowerCase() || undefined,
      error_category: 'cart errors',
      product_id: undefined,
      product_name: undefined,
      product_brand: undefined,
    },
  })

export const sendOrderInteractionEvent = ({
  action,
  detail,
  section,
  orderType,
  component,
  elementType,
  elementText,
  linkUrl,
  productId,
  productName,
  productBrand,
  productVariant,
}: {
  action: string
  detail?: string
  section?: string
  orderType?: string
  component?: string
  elementType?: string
  elementText?: string
  linkUrl?: string
  productId?: string
  productName?: string
  productBrand?: string
  productVariant?: string
}): void => {
  const basePayload = {
    event: 'order_interaction',
    action: action.toLowerCase(),
    detail: detail?.toLowerCase() || undefined,
    section: section?.toLowerCase(),
    order_type: orderType,
    component: component || 'body',
    element_type: elementType || 'link',
    element_text: elementText?.toLowerCase() || undefined,
    link_url: linkUrl?.toLowerCase(),
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId?.toLowerCase(),
    product_name: productName
      ?.toLowerCase()
      .replace(regexForStripHTMLWithContent, ''),
    product_brand: productBrand?.toLowerCase(),
    product_variant: productVariant?.toLowerCase(),
  }
  sendEvent({ payload: basePayload })
}

export const sendCommonDetailClickEvent = (
  ga4Payload: GA4CommonDetailClickEvent
): void => {
  const basePayload = {
    event: ga4Payload.event,
    action: ga4Payload.action,
    detail: ga4Payload.detail?.toLowerCase(),
    section: ga4Payload.section,
    component: ga4Payload.component,
    element_text: ga4Payload.elementText?.toLowerCase(),
    element_type: ga4Payload.elementType,
    link_url: ga4Payload.linkUrl,
    core_event: ga4Payload.coreEvent,
    user_detail: ga4Payload.userDetail,
    event_group: ga4Payload.eventGroup,
    event_subgroup: ga4Payload.eventSubGroup,
    product_id: ga4Payload.productId?.toLowerCase(),
    product_name: ga4Payload.productName?.toLowerCase(),
    product_brand: ga4Payload.productBrand?.toLowerCase(),
    product_variant: ga4Payload.productVariant,
  }
  sendEvent({ payload: basePayload })
}

export const sendHeaderAccountMenuEvents = (
  menu: string,
  linkUrl?: string
): void => {
  const basePayload = {
    event: EventType.Navigation_Interaction,
    action: 'header click',
    detail: `hello user > ${menu
      .replace(regexForStripSymbols, '')
      .toLowerCase()}`,
    section: undefined,
    component: 'header',
    element_type: 'link',
    element_text: menu.replace(regexForStripSymbols, '').toLowerCase(),
    link_url: linkUrl || undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
  }
  sendEvent({ payload: basePayload })
}

type HeaderMenuEvent = {
  menu: Message
  linkUrl?: string
  navMenu?: string
  navTreeOne?: string
}

export const sendHeaderMenuEvents = ({
  menu,
  linkUrl,
  navMenu = menu?.defaultMessage,
  navTreeOne,
}: HeaderMenuEvent): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: EventType.Navigation_Interaction,
      action: 'header click',
      detail: navTreeOne
        ? `${navMenu.toLowerCase()} > ${navTreeOne.toLowerCase()}`
        : navMenu?.toLowerCase(),
      section: undefined,
      component: 'header',
      element_type: 'link',
      element_text:
        navTreeOne?.toLowerCase() || navMenu?.toLowerCase() || undefined,
      link_url: linkUrl || undefined,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendPDPProductInfoClickedEvent = (
  ga4Payload?: GA4PDPClickPayload
): void => {
  // GA4
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'product_detail_page_click',
      click_type: ga4Payload?.clickType,
      click_action: ga4Payload?.clickAction,
      click_details: ga4Payload?.clickDetails,
      product_id: ga4Payload?.productId.toLowerCase(),
      product_name: ga4Payload?.productName
        .replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .toLowerCase(),
      product_brand: ga4Payload?.productBrand.toLowerCase(),
      component: ga4Payload?.component,
      element_type: ga4Payload?.elementType,
      link_text: ga4Payload?.linkText,
      link_url: ga4Payload?.linkUrl || undefined,
    },
  })
}

export const sendSearchedTermEvent = (
  eventLabel,
  ga4payload?: GA4SearchEventPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search',
      search_box_input: ga4payload?.searchTerm || 'no_user_input',
      search_term: ga4payload?.searchTerm,
      search_auto_suggest: ga4payload?.searchAutoSuggest,
      search_auto_suggest_term: ga4payload?.searchAutoSuggestTerm,
      search_type: ga4payload?.searchType,
      search_component: ga4payload?.searchComponent,
      search_error_message: ga4payload?.searchErrorMessage,
      search_result_counts: ga4payload?.searchResultCount,
      did_you_mean_terms: undefined,
    },
  })
}

export const getAdvancedSearchTypeValue = (
  type: string | undefined
): string | undefined => {
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (type) {
    case ProductSearchType.Product:
      return AdvancedSearchTypeEnum.Product
    case ProductSearchType.ProductExact:
      return AdvancedSearchTypeEnum.ProductExact
    case ProductSearchType.ProductNumber:
      return AdvancedSearchTypeEnum.ProductNumber
    case ProductSearchType.ProductName:
      return AdvancedSearchTypeEnum.ProductName
    case ProductSearchType.CasNumber:
      return AdvancedSearchTypeEnum.CasNumber
    case ProductSearchType.MolecularFormula:
      return AdvancedSearchTypeEnum.MolecularFormula
    case ProductSearchType.SubstanceId:
      return AdvancedSearchTypeEnum.SubstanceId
    case ProductSearchType.FemaNumber:
      return AdvancedSearchTypeEnum.FemaNumber
    case ProductSearchType.ColorIndex:
      return AdvancedSearchTypeEnum.ColorIndex
    case ProductSearchType.EgecNumber:
      return AdvancedSearchTypeEnum.EgecNumber
    case ProductSearchType.EcNumber:
      return AdvancedSearchTypeEnum.EcNumber
    case ProductSearchType.GeneProduct:
      return AdvancedSearchTypeEnum.GeneProduct
    case ProductSearchType.MdlNumber:
      return AdvancedSearchTypeEnum.MdlNumber
    case ProductSearchType.PubMed:
      return AdvancedSearchTypeEnum.PubMed
    case ProductSearchType.Protocol:
      return AdvancedSearchTypeEnum.Protocol
    case GeneSearchType.Gene:
      return AdvancedSearchTypeEnum.Gene
    case GeneSearchType.GeneSymbol:
      return AdvancedSearchTypeEnum.GeneSymbol
    case GeneSearchType.GeneId:
      return AdvancedSearchTypeEnum.GeneId
    case GeneSearchType.TrcNumber:
      return AdvancedSearchTypeEnum.TrcNumber
    case GeneSearchType.RefSeq:
      return AdvancedSearchTypeEnum.RefSeq
    case GeneSearchType.GeneDesc:
      return AdvancedSearchTypeEnum.GeneDesc
    default:
      return type
  }
}

export const sendAdvancedSearchedTermEvent = (
  ga4payload?: GA4SearchEventPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search',
      search_term: ga4payload?.searchTerm || 'no_user_input',
      search_auto_suggest: ga4payload?.searchAutoSuggest,
      search_auto_suggest_term: ga4payload?.searchAutoSuggestTerm,
      search_type: getAdvancedSearchTypeValue(ga4payload?.searchType),
      search_component: ga4payload?.searchComponent,
      search_error_message: ga4payload?.searchErrorMessage,
    },
  })
}

export const sendSearchInteractionEvent = (
  payload: GA4SearchInteractionPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search_interaction',
      action: payload.action,
      detail: payload.detail ?? undefined,
      section: payload.section ?? undefined,
      component: payload.component,
      element_type: payload.elementType,
      element_text: payload.elementText,
      link_url: payload.linkUrl,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendStructureSearchEvent = (
  ga4payload?: GA4SearchEventPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'search',
      search_term: ga4payload?.searchTerm || 'no_user_input',
      search_auto_suggest: ga4payload?.searchAutoSuggest,
      search_auto_suggest_term: ga4payload?.searchAutoSuggestTerm,
      search_type: getAdvancedSearchTypeValue(ga4payload?.searchType),
      search_component: ga4payload?.searchComponent,
      search_error_message: ga4payload?.searchErrorMessage,
    },
  })
}

export const sendEmptyRecommendationGA4Event = (
  payload: GA4EmptyRecommendationPayload
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'empty_recommendation',
      action: payload.action,
      detail: payload.detail || undefined,
      section: payload.section,
      product_id: payload.productId || undefined,
      product_brand: payload.productBrand || undefined,
      core_event: 'no',
      component: undefined,
      element_type: undefined,
      element_text: undefined,
      link_url: undefined,
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendShoppingAiInteractionEvent = (
  payload: shoppingAiInteractionPayload
): void => {
  sendEvent({
    payload: {
      event: 'ls_ai_interaction',
      action: payload.action,
      detail: payload.detail,
      section: payload.section,
      component: 'body',
      element_type: payload.element_type,
      element_text: payload.element_text,
      link_url: payload.link_url ?? undefined,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendCSRFiltersEvent = (ga4Payload: GA4CSRFiltersEvent): void => {
  const basePayload = {
    event: ga4Payload.event,
    action: ga4Payload.action?.toLowerCase(),
    detail: ga4Payload.detail?.toLowerCase(),
    section: ga4Payload.section,
    component: ga4Payload.component,
    element_text: ga4Payload.elementText?.toLowerCase(),
    element_type: ga4Payload.elementType,
    link_url: ga4Payload.linkUrl,
    core_event: ga4Payload.coreEvent,
    user_detail: ga4Payload.userDetail,
    event_group: ga4Payload.eventGroup,
    event_subgroup: ga4Payload.eventSubGroup,
    filter_name: ga4Payload.filterName?.toLowerCase(),
    filter_name_count: ga4Payload.filterNameCount,
    filter_category: ga4Payload.filterCategory?.toLowerCase(),
    filter_existing: ga4Payload.filterExisting?.trim().toLowerCase(),
  }
  sendEvent({ payload: basePayload })
}

export const sendSearchDidYouMeanEvent = (
  searchTerm: string,
  terms: Maybe<DidYouMeanTerm>[],
  focus?: string,
  type?: string,
  page?: string,
  qType?: string
): (() => void) => {
  const timeout = setTimeout(() => {
    try {
      // type isn't always sent in for legacy
      // but need it for ga4 - using 2nd type param
      const ga4Type = type ? type : qType
      // DidYouMeanTerms compoent slicing into 3
      const didYouMeanTerms = terms ? terms.slice(0, 3) : []
      const eventAction =
        didYouMeanTerms.length > 0
          ? EventValues.DidYouMean
          : EventValues.NoResults

      // GA4 No Results Found Event
      sendEvent({
        payload: {
          event: 'view_search_results',
          q_focus: focus,
          q_page: page || '1',
          q_type: ga4Type || undefined,
          search_result_status:
            eventAction === EventValues.NoResults
              ? 'no results page'
              : eventAction.toLowerCase(),
          search_term: searchTerm,
          did_you_mean_terms: didYouMeanTerms.length
            ? didYouMeanTerms
                .map((didYouMeanTerm) => didYouMeanTerm?.term)
                .join('|')
            : undefined,
        },
      })
    } catch (error) {
      console.error('No result / Did you mean - Event', error)
    }
  }, ONLOAD_EVENT_TIMEOUT)
  return () => {
    clearTimeout(timeout)
  }
}

export const sendBasicRegistrationEvent = (
  userId?: string | null,
  isOffersAndPromotionsEmail?: boolean | null
): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'sign_up',
      newsletter_subscription: isOffersAndPromotionsEmail
        ? EventValues.Yes
        : EventValues.No,
      method: 'website',
      newsLetterSignup: isOffersAndPromotionsEmail
        ? EventValues.Yes
        : EventValues.No,
      eventInteractionType: 0,
    },
  })
}

export const sendHeaderTopMenuEvents = (): void => {
  const logoClickGA4Payload = {
    event: EventType.Navigation_Interaction,
    action: 'header click',
    detail: 'home',
    section: undefined,
    component: 'header',
    element_type: 'link',
    element_text: 'milliporesigma',
    link_url: '/',
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
  }
  sendEvent({ payload: logoClickGA4Payload })
}

export const sendSessionErrorEvent = (OnloadEvent?: boolean): (() => void) => {
  const timeout = setTimeout(
    () => {
      const eventLabel = messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED
        ? messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED?.defaultMessage
        : EventValues.SessionErrorMessage
      sendEvent({
        eventType: GTMEventTypes.AnalyticsEvent,
        payload: {
          event: 'exception',
          description: eventLabel.toLowerCase(),
          error_category: EventValues.SessionErrors.toLowerCase(),
          product_id: undefined,
          product_name: undefined,
          product_brand: undefined,
        },
      })
    },
    OnloadEvent ? ONLOAD_EVENT_TIMEOUT : 0
  )
  return () => {
    clearTimeout(timeout)
  }
}

export const setGlobalObject = (
  items: CartItemFragment[] | undefined,
  totals?: CartTotalsFragment | null,
  order?: OrderDetailsFragment,
  page?: string
): void => {
  try {
    type GlobalObjectType = {
      tax: number | null
      shipping: number | null
      revenue: string
      currencyCode: string
      orderNumber: string | null | undefined
      productName: string[]
      productPrice: number[]
      productId: string[]
      productBrand: string[]
      productVariant: string[]
      productQuantity: string[]
    }

    let currencyCode = items?.length
      ? items[0]?.currency || DEFAULT_CURRENCY_CODE
      : DEFAULT_CURRENCY_CODE
    const orderItems = order?.items.map((item) => {
      currencyCode = item?.currency || DEFAULT_CURRENCY_CODE
      return {
        ...item,
        pricing: {
          price:
            priceFormatter(item?.pricePerUnit) ||
            priceFormatter(item?.listPrice),
        },
      }
    })

    // If order is given use its sales tax, otherwise go to totals.
    // Both are fed to price formatted which it totals tax isn't defined you'll get the correct null response.
    const taxAmount = order ? order?.salesTax : totals?.tax
    const shippingAmount = order ? order?.transHandlingCharge : totals?.shipping

    const globalObject: GlobalObjectType = {
      tax: priceFormatter(taxAmount),
      shipping: priceFormatter(shippingAmount),
      revenue: order
        ? order.total.toString()
        : totals
          ? totals?.total.toString()
          : '0.00',
      orderNumber: order ? order.orderNumber : EventValues.Empty,
      currencyCode: getCurrencyCode(currencyCode?.toString()),
      productName: [],
      productPrice: [],
      productId: [],
      productBrand: [],
      productVariant: [],
      productQuantity: [],
    }

    const products = clubbingUpProducts(order ? orderItems : items, page) || []
    products.forEach((item) => {
      globalObject.productName.push(item.name)
      globalObject.productPrice.push(priceFormatter(item?.price) || 0.0)
      globalObject.productId.push(item?.id)
      globalObject.productBrand.push(item?.brand)
      globalObject.productVariant.push(item?.variant)
      globalObject.productQuantity.push(item?.quantity.toString())
    })
    sessionStorage.setItem<GlobalObjectType>('PRODUCTS_OBJECT', globalObject)
  } catch (error) {
    console.error('ERROR - Saving Global Object: ', error)
  }
}

export const sendProductDetailEvent = (
  products?: ValidMaterialPricingDetailFragment[],
  productName?: string,
  productNumber?: string,
  brandKey?: string,
  isMarketplace?: boolean,
  erpType?: string[],
  userType?: Object
): (() => void) => {
  const currency = products?.length
    ? products[0]?.currency
    : DEFAULT_CURRENCY_CODE
  const materialDescription = products?.length
    ? products[0]?.materialDescription?.length
      ? products[0]?.materialDescription
      : productName
    : productName

  const productMaterialName =
    (materialDescription || EventValues.Empty)
      .toLowerCase()
      .replace(regexForStripEncodingUnicode, '')
      .replace(regexForStripHTML, '')
      .replace(regexForStripSymbols, '') || ''

  const timeout = setTimeout(() => {
    const item = {
      item_id: (productNumber || EventValues.Empty).toLowerCase(),
      item_name: productMaterialName,
      item_brand: (brandKey || EventValues.Empty).toLowerCase(),
      item_type: isMarketplace ? 'marketplace' : 'standard',
    }

    if (isGoogleTagManagerEnabled) {
      // GA4 - View item details
      sendEvent({
        payload: {
          event: 'view_item',
          ecommerce: {
            currency: getCurrencyCode(currency || DEFAULT_CURRENCY_CODE),
            items: [{ ...item }],
          },
        },
      })
    }

    if (isTealiumEnabled) {
      productDetailPageViewTrack(
        brandKey,
        productNumber,
        productMaterialName,
        erpType,
        userType
      )
    }
  }, ONLOAD_EVENT_TIMEOUT)

  return () => {
    clearTimeout(timeout)
  }
}

export const sendViewSearchResultsEvent = (
  ga4payload?: GA4ViewSearchResultsPayload
): void => {
  sendEvent({
    payload: {
      event: 'view_search_results',
      q_focus: ga4payload?.qFocus,
      q_page: ga4payload?.qPage,
      q_type: ga4payload?.qType,
      search_result_status: ga4payload?.searchResultStatus,
      search_term: ga4payload?.searchTerm,
      did_you_mean_terms: ga4payload?.didYouMeanTerms,
      search_results_count:
        ga4payload?.searchResultsCount?.toString() || 'no result count',
    },
  })
}

export const determinePAForAddtoCart = (path): AddToCartPagesEnum => {
  switch (path) {
    case SearchFocusType.BuildingBlocks:
      return AddToCartPagesEnum.BuildingBlockSearchResults
    case SearchFocusType.Products:
      return AddToCartPagesEnum.SearchResultPage
    case SearchFocusType.StructureSearch:
      return AddToCartPagesEnum.StructureSearchResults
    default:
      return AddToCartPagesEnum.Others
  }
}

export const getSearchFocusEventValue = (focus): string => {
  switch (focus) {
    case SearchFocusType.BuildingBlocks:
      return SearchPageEnum.BuildingBlocks
    case SearchFocusType.TechnicalDocuments:
      return SearchPageEnum.TechnicalDocuments
    case SearchFocusType.StructureSearch:
      return SearchPageEnum.StructureSearch
    case SearchFocusType.SiteContent:
      return SearchPageEnum.SiteContent
    default:
      return focus
  }
}

export const sendEmproveDownloadsEvent = (
  document,
  linkUrl?,
  isPaidSubscription?
): void => {
  const { displayType, product, fileName } = document
  const [file, version, fileExtension] = fileName.split('.')
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'file_download',
      file_extension: fileExtension,
      file_name: `${file}.${version}`.toLowerCase(),
      file_category: `emprove - ${isPaidSubscription ? 'paid' : 'free'}`,
      product_id: product.toLowerCase(),
      error_description: undefined,
      component: 'body',
      element_type: 'link',
      link_text: displayType.toLowerCase(),
      link_url: linkUrl || undefined,
    },
  })
}

export const sendOrderConfirmationEventForB2B = ({
  callback,
}: {
  callback?: () => void
} = {}): void => {
  const data: GTMEcommerceValues | null =
    sessionStorage.getItem<GTMEcommerceValues>(CART_DATA_STORAGE_KEY)
  try {
    if (!data) throw new Error()

    if (isTealiumEnabled) {
      sendOrderConfirmationB2BTrackForAnalytic({ data, callback })
    }
  } catch (error) {
    console.error('ERROR - B2B Order Confirmation Event ', error)
  }
}

export const sendPurchaseConfirmationEventForB2B = (): void => {
  const payload: GTMEcommerceValues | null =
    sessionStorage.getItem<GTMEcommerceValues>(CART_DATA_STORAGE_KEY)
  const subTotal = payload?.ecommerce?.purchase?.products.reduce(
    (acc, item) => (Number(item.price) * item.quantity || 0) + acc,
    0
  )

  // UA/GA3 legacy event send to delivery only one payload to one platform based on object data flags.
  //This can be replaced with a sendEvent() or removed entirely once UA/GA3 is sunset on July 1st, 2024
  sendEvent({
    payload: {
      addtocart_type: 'default',
      event: 'purchase',
      order_type: 'b2b-standard',
      deep_order_id: EventValues.Empty,
      order_participant_id: payload?.purchaseParticipantId || EventValues.Empty,
      order_sold_to: payload?.purchaseSoldToNumber || EventValues.Empty,
      order_bill_to: payload?.purchaseBillToNumber || EventValues.Empty,
      order_ship_to: payload?.purchaseShipToNumber || EventValues.Empty,
      shipping_tier:
        payload?.ecommerce?.purchase?.actionField?.shippingMethod ||
        EventValues.Empty,
      payment_type: 'b2b',
      packaging_handling: priceFormatter(payload?.packageCharge),
      tariff_surcharge: priceFormatter(payload?.tariffSurcharge),
      ecommerce: {
        transaction_id: payload?.eventAction || EventValues.Empty,
        value: subTotal || 1,
        tax: priceFormatter(payload?.ecommerce?.purchase?.actionField?.tax),
        shipping: priceFormatter(
          payload?.ecommerce?.purchase?.actionField?.shipping
        ),
        coupon: payload?.ecommerce?.purchase?.actionField?.coupon,
        currency: payload?.ecommerce?.currencyCode,
        items:
          payload?.ecommerce?.purchase?.products?.map((product) => ({
            item_id: product.id,
            item_name: product.name,
            coupon: product.coupon || EventValues.Empty,
            discount: product.discount ?? 0,
            item_brand: product.brand,
            item_variant: product.variant,
            price: priceFormatter(product?.price),
            item_type: product.dimension91,
            quantity: product.quantity ?? 1,
          })) || [],
      },
    },
  })
}

export const sendErrorPageEvent = (
  statusCode?: number,
  description?: string
): (() => void) => {
  const timeout = setTimeout(() => {
    const errorStatusCode = statusCode ? `${statusCode}-` : ''
    const errorDescription = description
      ? `${errorStatusCode}${description?.toLowerCase()}`
      : undefined

    const basePayload = {
      event: 'exception',
      description: errorDescription,
      error_category: 'page errors',
      product_id: undefined,
      product_name: undefined,
      product_brand: undefined,
    }
    sendEvent({ payload: basePayload })
  }, ONLOAD_EVENT_TIMEOUT)
  return () => {
    clearTimeout(timeout)
  }
}

export const sendDownloadFileEvent = (
  event: DownloadFileEvent,
  ga4Payload: GA4DownloadFilePayload
): void => {
  const isPDF =
    event.action === EventActions.SDS ||
    event.action === EventValues.OrderConfirmationPDF ||
    event.action === EventActions.Invoices ||
    ga4Payload.linkUrl?.endsWith('.pdf')
  const label = ((): string => {
    switch (event.action) {
      case EventActions.SDS:
        return [event.brandKey, event.productNumber, event.language]
          .join('-')
          .toLowerCase()
      case EventActions.Quotes:
        return event.quoteNumber.toLowerCase()
      case EventActions.COA:
        return [event.brandKey, event.productNumber].join('-').toLowerCase()
      case EventActions.SpecificationSheet:
      case EventActions.ProductInformationSheet:
        return ga4Payload.linkUrl?.split('/').pop() || ''
      case 'more documents':
      case 'invoices':
      case EventValues.OrderConfirmationPDF:
        if (isPDF) {
          const fileName =
            ga4Payload.fileName?.split('/').pop() ||
            ga4Payload.linkUrl?.split('/').pop()
          return fileName?.substring(0, fileName.length - 4) || ''
        } else {
          return ga4Payload.linkUrl?.substring(6) || ''
        }
      case 'export orders list':
        return 'order search'
      case 'bulk upload template':
        return 'quick_order_template'
      default:
        return ''
    }
  })()

  const basePayload = {
    event: 'file_download',
    file_extension: isPDF ? 'pdf' : ga4Payload.fileExtension || 'html',
    file_name: label || undefined,
    file_category: ga4Payload.fileCategory || event.action,
    product_id: ga4Payload.productId?.toLowerCase() || undefined,
    lot_number: ga4Payload.lotNumber?.toLowerCase() || undefined,
    error_description: ga4Payload.errorDescription || undefined,
    component: ga4Payload.component,
    element_type: ga4Payload.elementType,
    link_url: ga4Payload.linkUrl || undefined,
    link_text: ga4Payload.linkText.toLowerCase(),
    ...(ga4Payload.section ? { section: ga4Payload.section } : {}),
  }
  sendEvent({ payload: basePayload })
}

export const sendMenuNavigationEvent = ({
  L1item = undefined,
  L2item = undefined,
  L3item = undefined,
  L4item = undefined,
  linkUrl,
}: Menu): void => {
  const navItems = [L1item, L2item, L3item, L4item]
    .map((item) =>
      item
        ?.replace(regexForStripHTML, '')
        ?.replace(regexForStripEncodingUnicode, '')
        ?.replace(regexForStripSymbols, '')
        .trim()
        .toLowerCase()
    )
    .filter((item) => item)

  const basePayload = {
    event: EventType.Navigation_Interaction,
    action: 'header click',
    detail: navItems.join(' > '),
    section: undefined,
    component: 'header',
    element_type: 'link',
    element_text: navItems[navItems.length - 1],
    link_url: linkUrl || undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
  }
  sendEvent({ payload: basePayload })
}

export const getVisibleRecommendedProducts = (
  recommendedProductsParent
): RecommendedProduct[] => {
  if (recommendedProductsParent) {
    const recommendedProducts = [
      ...recommendedProductsParent.querySelectorAll('.promotions-slider-div'),
    ]
    if (recommendedProducts.length) {
      const recommendedProductsList: RecommendedProduct[] = []
      recommendedProducts.forEach((item, index) => {
        const itemLink = item
          .querySelector('a[id^="product-card-"]')
          ?.getAttribute('href')
          ?.split('/')[5]
        if (itemLink) {
          const productNumber = itemLink?.split('?')[0]
          const product: RecommendedProduct = {
            id: `list-item-${index + 1}`,
            isVisible: item?.parentElement?.className.includes('slide-visible'),
            productNumber,
          }
          recommendedProductsList.push(product)
        }
      })
      return recommendedProductsList.filter(
        (visibleProduct) => visibleProduct.isVisible
      )
    } else {
      return []
    }
  } else {
    return []
  }
}

export const sendCustomProductDetailEvent = (
  product?: PdpFieldsFragment
): void => {
  const productBrand = product?.brand?.key || EventValues.Empty
  const productNumber = product?.productNumber || EventValues.Empty
  const productName = product?.name || EventValues.Empty

  const item = {
    item_id: productNumber.toLowerCase(),
    item_name:
      productName
        .toLowerCase()
        .replace(regexForStripEncodingUnicode, '')
        .replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '') || '',
    item_brand: productBrand.toLowerCase(),
    item_type: product?.isMarketplace ? 'marketplace' : 'standard',
  }
  // GA4 - View item details
  sendEvent({
    payload: {
      event: 'view_item',
      ecommerce: {
        currency: DEFAULT_CURRENCY_CODE.toLowerCase(),
        items: [{ ...item }],
      },
    },
  })
}

export const sendBreadcrumbsEvent = (
  eventAction,
  eventLabel,
  linkUrl: string,
  showBack?: Boolean
): void => {
  // sometimes they are strings
  // sometimes they are formattedmessages
  // defaultMessage because it needs to be in English
  const combinedBreadCrumbs = isArray(eventLabel)
    ? eventLabel
        .map((item) => {
          return typeof item === 'string'
            ? item
            : item?.props?.defaultMessage || ''
        })
        .join(' > ')
        .toLowerCase()
    : undefined

  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: EventType.Navigation_Interaction,
      action: 'breadcrumb click',
      detail: showBack ? `back > ${combinedBreadCrumbs}` : combinedBreadCrumbs,
      section: undefined,
      component: 'breadcrumb',
      element_type: 'link',
      element_text:
        eventAction?.props?.defaultMessage?.toLowerCase() ||
        eventAction?.toLowerCase() ||
        undefined,
      link_url: linkUrl || undefined,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendSearchCertificatesEvent = ({
  section,
  productNumber,
  lotNumber,
  errorStatus = false,
  linkUrl,
}: {
  section: string
  productNumber: string
  lotNumber: string
  errorStatus?: boolean
  linkUrl?: string
}): void => {
  const filename = productNumber.replace(/\./g, '-')

  const basePayload = {
    event: 'file_download',
    file_extension: errorStatus ? undefined : linkUrl ? 'html' : 'pdf',
    file_name: errorStatus
      ? undefined
      : linkUrl
        ? lotNumber.trim().toLowerCase()
        : filename,
    file_category: section.toLowerCase(),
    product_id: productNumber.trim().toLowerCase(),
    lot_number: lotNumber.trim().toLowerCase(),
    error_description: errorStatus
      ? `${section.toLowerCase()} not found`
      : undefined,
    component: 'action_form',
    element_type: 'button',
    link_text: 'search',
    link_url: linkUrl || undefined,
  }
  sendEvent({ payload: basePayload })
}

export const sendCertificatesDownloadEvent = ({
  fileExtension,
  productNumber,
  lotNumber,
}: {
  fileExtension: string
  productNumber: string
  lotNumber: string
}): void => {
  sendEvent({
    payload: {
      event: 'file_download',
      file_extension: fileExtension,
      file_name: productNumber.trim().toLowerCase(),
      file_category: 'coa',
      product_id: productNumber.trim().toLowerCase(),
      lot_number: lotNumber.trim().toLowerCase(),
      error_description: undefined,
      component: 'left rail',
      element_type: 'link',
      link_text: lotNumber.trim().toLowerCase(),
      link_url: undefined,
    },
  })
}

export const sendViewSampleCOAEvent = (
  ga4payload: ViewSampleCOAEvent
): void => {
  sendEvent({
    payload: {
      event: 'file_download',
      file_extension: ga4payload?.fileExtension,
      file_name: ga4payload?.fileName,
      file_category: 'sample coa',
      product_id: ga4payload?.productId,
      error_description: undefined,
      component: 'action_form',
      element_type: 'button',
      link_text: 'view sample coa',
      link_url: ga4payload?.linkUrl || undefined,
    },
  })
}

export const sendMemberInviteEvent = ({
  detail = undefined,
  component,
  elementType,
  elementText,
  contentType,
  method,
}: {
  detail: string | undefined
  component: string
  elementType: string
  elementText: string
  contentType: string
  method: string
}): void => {
  const basePayload = {
    event: 'share',
    content_type: contentType,
    method,
    detail,
    component,
    element_type: elementType,
    element_text: elementText,
  }
  sendEvent({ payload: basePayload })
}

export const sendShareEvent = ({
  contentType,
  component,
  elementType,
  elementText,
  method,
  detail,
}: {
  contentType: string
  component: string
  elementType: string
  elementText: string
  method: string
  detail?: string
}): void => {
  const basePayload = {
    event: 'share',
    content_type: contentType,
    element_type: elementType,
    element_text: elementText,
    detail,
    method,
    component,
  }
  sendEvent({ payload: basePayload })
}

export const sendProductDetailsEvent = (
  actionType: ProductDetailsEventAction,
  ga4Payload?: GA4ProductDetailsEventPayload
): void => {
  const payloadBase = {
    event: ga4Payload?.event || 'pdp_clicks',
    click_action: ga4Payload?.clickAction || actionType,
    click_type: ga4Payload?.clickType,
    click_details: ga4Payload?.clickDetails || undefined,
    element_type: ga4Payload?.elementType,
    component: ga4Payload?.component,
    link_text: ga4Payload?.linkText,
  }

  if (actionType === ProductDetailsEventAction.ViewSampleCOAError) {
    sendEvent({
      payload: {
        ...payloadBase,
        eventInteractionType: 0,
      },
    })

    return // Void return if COA data was empty so main event doesn't fire
  }

  sendEvent({
    payload: {
      ...payloadBase,
      click_details: ga4Payload?.clickDetails || undefined,
      product_id: ga4Payload?.productId?.toLowerCase() || undefined,
      product_name:
        ga4Payload?.productName
          ?.replace(regexForStripSymbols, '')
          .replace(regexForStripHTML, '')
          .toLowerCase() || undefined,
      product_brand: ga4Payload?.productBrand?.toLowerCase() || undefined,
      product_variant: ga4Payload?.productVariant?.toLowerCase() || undefined,
      link_url: ga4Payload?.linkUrl || undefined,
    },
  })
}

export const sendProductInfoInteractionEvent = ({
  action,
  detail,
  section,
  component,
  elementType,
  elementText,
  linkUrl,
  productId,
  productBrand,
  productVariant,
}: {
  action?: string
  detail?: string | null
  section?: string
  component: string
  elementType: string | undefined
  elementText?: string | null
  linkUrl?: string
  productId?: string
  productBrand?: string | null
  productVariant?: string
}): void => {
  const basePayload = {
    event: 'product_info_interaction',
    action: action?.toLowerCase(),
    detail:
      detail
        ?.replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .replace(regexForStripEncodingUnicode, '')
        .toLowerCase() || undefined,
    section,
    component,
    element_type: elementType,
    element_text: elementText?.toLowerCase() || undefined,
    link_url: linkUrl,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId?.toLowerCase() || undefined,
    product_brand: productBrand?.toLowerCase() || undefined,
    product_variant: productVariant?.toLowerCase(),
  }
  sendEvent({ payload: basePayload })
}

export const sendQuickOrderTracking = (
  ga4payload?: GA4QuickOrderTrackingPayload
): void => {
  //GA4
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'quick_order_page_click',
      click_type: ga4payload?.clickType,
      click_action: ga4payload?.clickAction,
      click_details: ga4payload?.clickDetails || undefined,
      component: ga4payload?.component,
      element_type: ga4payload?.elementType,
      link_text: ga4payload?.linkText,
      link_url: ga4payload?.linkUrl || undefined,
    },
  })
}

export const sendQuickOrderInteractionEvent = (
  ga4payload: Omit<GA4QuickOrderInteractionPayload, 'event'>,
  productDetails?: ProductDetails
): void => {
  const productPayload = productDetails
    ? {
        product_id: productDetails?.productId?.toLowerCase(),
        product_brand: productDetails?.productBrand?.toLowerCase(),
        product_variant: productDetails?.productVariant?.toLowerCase(),
        product_name: productDetails?.productName
          ?.replace(regexForStripHTML, '')
          .replace(regexForStripSymbols, '')
          .replace(regexForStripEncodingUnicode, '')
          .toLowerCase(),
      }
    : {}

  const basePayload = {
    event: 'quick_order_interaction',
    action: ga4payload?.action,
    detail: ga4payload?.detail,
    section: 'quick order',
    component: ga4payload?.component,
    element_type: ga4payload?.elementType,
    element_text: ga4payload?.elementText || undefined,
    link_url: undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
  }

  // enhancedPayload created beacuse for cross icon click from manual tab search in Quick Order Page required Product data
  // Manual tab and Bulk order click doed not required product data
  const enhancedPayload = productDetails
    ? { ...basePayload, ...productPayload }
    : basePayload

  // GA4 2.0 for Manual Tab, Bluk order and cross icon click in Quick Order Page
  sendEvent({
    payload: enhancedPayload,
  })
}

export const sendCommonDetailProductIdEvent = (
  ga4Payload: GA4CommonDetailProductIdEvent
): void => {
  const basePayload = {
    event: ga4Payload.event,
    action: ga4Payload.action?.toLowerCase(),
    detail: ga4Payload.detail?.toLowerCase(),
    section: ga4Payload.section,
    component: ga4Payload.component,
    element_text: ga4Payload.elementText?.toLowerCase(),
    element_type: ga4Payload.elementType,
    link_url: ga4Payload.linkUrl,
    core_event: ga4Payload.coreEvent,
    product_id: ga4Payload.productId?.toLowerCase(),
    user_detail: ga4Payload.userDetail,
    event_group: ga4Payload.eventGroup,
    event_subgroup: ga4Payload.eventSubGroup,
  }
  sendEvent({ payload: basePayload })
}

export const sendBeginOrCompleteQuoteRequestEvent = (
  ga4payload: GA4QuickOrderInteractionPayload,
  productDetails?: ProductDetails
): void => {
  const productPayload = productDetails
    ? {
        product_id: productDetails?.productId?.toLowerCase(),
        product_brand: productDetails?.productBrand?.toLowerCase(),
        product_variant: productDetails?.productVariant?.toLowerCase(),
      }
    : {}

  const basePayload = {
    event: ga4payload.event,
    action: undefined,
    detail: ga4payload?.detail || undefined,
    section: ga4payload?.section,
    component: ga4payload?.component,
    element_type: ga4payload?.elementType,
    element_text: ga4payload?.elementText,
    link_url: undefined,
    core_event: 'yes',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
  }

  const buildPayload = productDetails
    ? { ...basePayload, ...productPayload }
    : basePayload

  sendEvent({
    payload: buildPayload,
  })
}

export const sendQuoteInteractionEvent = ({
  action,
  detail,
  section,
  component,
  elementType,
  elementText,
  linkUrl,
  quoteStatus,
}: {
  action: string
  detail?: string | null
  section: string | undefined
  component: string
  elementType: string
  elementText?: string
  linkUrl?: string | null
  quoteStatus?: string
}): void => {
  const basePayload = {
    event: 'quote_interaction',
    action,
    detail: detail || undefined,
    section,
    component,
    element_type: elementType,
    element_text: elementText || undefined,
    link_url: linkUrl || undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    quote_status: quoteStatus || undefined,
  }
  sendEvent({ payload: basePayload })
}

export const sendSelectItemEvent = (
  item: string,
  productName?: string
): void => {
  const itemInfo = item.split('/')
  const itemId = itemInfo[3]
  const itemBrand = itemInfo[2]
  const commonPayload = {
    item_id: itemId.toLowerCase(),
    item_name: decodeTitle(productName?.toLowerCase() ?? ''),
    index: 1,
    item_brand: decodeTitle(itemBrand.toLowerCase() ?? ''),
    item_substance: undefined,
    item_list_id: 'pricing card',
    item_list_name: 'pricing card',
    item_substance_position: undefined,
  }
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'select_item',
      ecommerce: {
        item_list_id: 'pricing card',
        item_list_name: 'pricing card',
        items: [commonPayload],
      },
    },
  })
}

export const sendNewSrpTabSwitchEvent = ({
  originalTab,
  newTab,
  searchTerm,
  facets,
  totalCount,
  searchResultDisplay,
  facetCount,
}): void => {
  sendEvent({
    payload: {
      event: 'filter_interaction',
      action: 'change search type',
      detail: `tab switch from ${originalTab}`,
      section: undefined,
      component: 'body',
      element_type: 'tab',
      element_text: newTab,
      link_url: undefined,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subroup: undefined,
      filter_name: newTab,
      filter_name_count: facetCount,
      filter_category: 'search within',
      filter_existing: facets,
      search_term: searchTerm,
      search_type: newTab,
      search_result_count: totalCount,
      search_result_display: searchResultDisplay,
    },
  })
}

export const sendNewSrpProductClickEvent = ({
  product,
  elementType,
  substanceId,
  substanceName,
  productPosition,
}: NewSrpProductAnalytics): void => {
  const commonPayload = {
    item_id: product.productNumber?.toLowerCase(),
    item_name: decodeTitle(substanceName?.toLowerCase() ?? ''),
    index: productPosition ?? '',
    item_brand: decodeTitle(product?.brandKey?.toLowerCase() ?? ''),
    item_substance: substanceId,
    item_substance_position: productPosition?.toString(),
    item_list_id: GTMEventCategory.SearchResultPage.toLowerCase(),
    item_list_name: GTMEventCategory.SearchResultPage.toLowerCase(),
    item_element_type: elementType,
  }

  sendEvent({
    payload: {
      event: 'select_item',
      ecommerce: {
        item_list_id: GTMEventCategory.SearchResultPage?.toLowerCase(),
        item_list_name: GTMEventCategory.SearchResultPage?.toLowerCase(),
        items: [commonPayload],
      },
    },
  })
}

export const sendSrpExperienceToggleEvent = (toggleDetail: string): void => {
  sendEvent({
    payload: {
      event: 'search_interaction',
      action: 'change srp version',
      detail: toggleDetail || '',
      section: undefined,
      component: 'banner',
      element_type: 'toggle',
      element_text: undefined,
      link_url: 'link url',
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })
}

export const sendProductClickEvent = ({
  product,
  searchTerm,
  substanceId,
  substancePosition,
}: ProductAnalytics): void => {
  const commonPayload = {
    item_id: product.productNumber?.toLowerCase(),
    item_name: decodeTitle(product?.description?.toLowerCase() ?? ''),
    index: product?.position ?? '',
    item_brand: decodeTitle(product?.brand?.key?.toLowerCase() ?? ''),
    item_substance: substanceId,
    item_list_id: GTMEventCategory.SearchResultPage.toLowerCase(),
    item_list_name: GTMEventCategory.SearchResultPage.toLowerCase(),
    item_substance_position: substancePosition?.toString(),
  }
  sendEvent({
    payload: {
      event: 'select_item',
      search_term: searchTerm || 'no_user_input',
      ecommerce: {
        item_list_id: GTMEventCategory.SearchResultPage?.toLowerCase(),
        item_list_name: GTMEventCategory.SearchResultPage?.toLowerCase(),
        items: [commonPayload],
      },
    },
  })
}

export type EventAction =
  | 'close'
  | 'ignore'
  | 'open button'
  | 'open link'
  | 'show products'

export const sendSelectorToolEvent = ({
  eventAction,
  eventLabel,
  action,
  detail,
  section,
  component,
  elementType,
  elementText,
  productId,
  productName,
  productBrand,
  basePayloadOverrides = {},
}: {
  eventAction?: EventAction
  eventLabel?: string
  action: string
  detail?: string
  section?: string
  component?: string
  elementType?: string
  elementText?: string
  productId?: string
  productName: string | undefined
  productBrand?: string
  basePayloadOverrides?: Record<string, unknown>
}): void => {
  const basePayload = {
    event: 'antibody_selector_interaction',
    action,
    detail: detail?.toLowerCase() || undefined,
    section,
    component,
    element_type: elementType,
    element_text: elementText?.toLowerCase() || undefined,
    link_url: undefined,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId ? productId?.toLowerCase() : undefined,
    product_name: productName
      ? productName
          ?.replace(regexForStripHTML, '')
          .replace(regexForStripSymbols, '')
          .replace(regexForStripEncodingUnicode, '')
          .toLowerCase()
      : undefined,
    product_brand: productBrand ? productBrand?.toLowerCase() : undefined,
    product_variant: undefined,
    ...basePayloadOverrides,
  }
  sendEvent({ payload: basePayload })
}

export const sendProductDetailPromoEvent = ({
  event,
  component,
  promoPhrase,
  creativeName,
  product,
  materialNumber,
}: ProductDetailPromoPayload): void => {
  sendEvent({
    payload: {
      event,
      ecommerce: {
        creative_slot: `${promoPhrase} ${component} - pdp`,
        items: [
          {
            creative_name: creativeName,
            index: 1,
            item_brand: product?.brand?.key.toLowerCase(),
            item_id: product?.productNumber?.toLowerCase(),
            item_variant: materialNumber?.toLowerCase(),
            item_list_id: `${promoPhrase} - ${component}`,
            item_list_name: `${promoPhrase} - ${component}`,
            promotion_id: promoPhrase,
            promotion_name: promoPhrase,
          },
        ],
      },
    },
  })
}

export const sendVideoInteractionEvent = ({
  action,
  videoCurrentTime,
  videoDuration,
  videoPercent,
  videoUrl,
  visible,
  productId,
  productName,
  productBrand,
}: {
  action: string
  videoCurrentTime: number
  videoDuration: number
  videoPercent: number
  videoUrl: string
  visible: boolean
  productId: string | undefined
  productName: string | undefined
  productBrand: string | null | undefined
}): void => {
  const videoUrlSection = videoUrl.split('/')
  sendEvent({
    payload: {
      event: `video_${action}`,
      video_current_time: videoCurrentTime,
      video_duration: videoDuration,
      video_percent: videoPercent,
      video_provider: videoUrlSection[1],
      video_title: videoUrlSection[videoUrlSection.length - 1],
      video_url: videoUrl,
      visible,
      product_id: productId?.toLowerCase() || undefined,
      product_name:
        productName
          ?.replace(regexForStripHTML, '')
          .replace(regexForStripSymbols, '')
          .replace(regexForStripEncodingUnicode, '')
          .toLowerCase() || undefined,
      product_brand: productBrand?.toLowerCase() || undefined,
    },
  })
}

export const analyticsEnTabName = (tabKey): DocumentLibraryTab | undefined => {
  switch (tabKey) {
    case 'RECENTLY_PURCHASED':
      return DocumentLibraryTab.RecentlyPurchase
    case 'RECENTLY_VIEWED':
      return DocumentLibraryTab.RecentlyViewed
    case 'YOUR_DOCUMENTS':
      return DocumentLibraryTab.YourDocuments
    case 'SAVED_PROTOCOLS':
      return DocumentLibraryTab.ProtocolsAndArticles
    default:
      return undefined
  }
}

export const sendErrorEvent = (ga4Payload: GA4ErrorEvent): void => {
  const basePayload = {
    event: ga4Payload.event || 'exception',
    description: ga4Payload.description?.toLowerCase(),
    error_category: ga4Payload.errorCategory?.toLowerCase(),
    product_id: ga4Payload.productId?.toLowerCase() || undefined,
    product_brand: ga4Payload.productBrand?.toLowerCase() || undefined,
    product_variant: ga4Payload.productVariant?.toLowerCase() || undefined,
    product_name:
      ga4Payload.productName
        ?.replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .replace(regexForStripEncodingUnicode, '')
        .toLowerCase() || undefined,
    lot_number: ga4Payload.lotNumber,
    cart_type: ga4Payload.cartType,
  }
  sendEvent({ payload: basePayload })
}

export const sendSignInInteractionEvents = ({
  action,
  section,
  element_type,
  element_text,
  link_url,
  component = 'card',
}: {
  action: string
  section: string
  element_type: string
  element_text: string
  link_url?: string
  component?: string
}): void =>
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: 'sign_in_interaction',
      action,
      section,
      component,
      element_type,
      element_text,
      link_url,
      core_event: 'no',
      user_detail: undefined,
      event_group: undefined,
      event_subgroup: undefined,
    },
  })

export const sendRegisterErrorEvent = (ga4Payload: GA4ErrorEvent): void => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      event: ga4Payload.event || 'exception',
      description: ga4Payload.description?.toLowerCase(),
      error_category: 'registration errors',
      product_id: undefined,
      product_brand: undefined,
      product_variant: undefined,
      product_name: undefined,
      lot_number: undefined,
      cart_type: undefined,
    },
  })
}

export const sendProductAiEvent = ({ product, index }: ProductCardProps) => {
  const { brand, productNumber, productName } = product
  const commonPayload = {
    item_id: productNumber.toLowerCase(),
    item_name:
      productName
        ?.replace(regexForStripEncodingUnicode, '')
        .replace(regexForStripHTML, '')
        .replace(regexForStripSymbols, '')
        .toLowerCase() || undefined,
    index: index,
    item_brand: brand.toLowerCase(),
    item_substance: undefined,
    item_substance_position: undefined,
    item_list_id: 'ai assistant',
    item_list_name: 'ai assistant',
  }
  sendEvent({
    payload: {
      event: 'select_item',
      ecommerce: {
        item_list_id: 'ai assistant',
        item_list_name: 'ai assistant',
        items: [commonPayload],
      },
    },
  })
}

export interface DocumentationImpressionPayload {
  action?: string
  basePayloadOverrides?: Record<string, unknown>
  component?: string
  detail?: string
  elementText?: string
  elementType?: string
  linkUrl?: string
  productId?: string
  section?: string
}

export const sendDocumentationImpressionEvent = ({
  action,
  basePayloadOverrides = {},
  component,
  detail,
  elementText,
  elementType,
  linkUrl,
  productId,
  section,
}: DocumentationImpressionPayload): void => {
  const basePayload = {
    event: 'documentation_impression',
    action: action?.toLowerCase() || undefined,
    detail: detail?.toLowerCase()?.trim() || undefined,
    section: section || 'documentation',
    component: component || 'body',
    element_type: elementType,
    element_text: elementText?.toLowerCase() || undefined,
    link_url: linkUrl,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId?.toLowerCase() || undefined,
    ...basePayloadOverrides,
  }

  sendEvent({ payload: basePayload })
}

export interface PricingOverrideEventPayload {
  action?: string
  basePayloadOverrides?: Record<string, unknown>
  component?: string
  detail?: string
  elementText?: string
  elementType?: string
  linkUrl?: string
  productBrand?: string
  productId?: string
  section?: string
}

export const sendPricingOverrideEvent = ({
  action,
  basePayloadOverrides = {},
  component,
  detail,
  elementText,
  elementType,
  linkUrl,
  productBrand,
  productId,
  section,
}: PricingOverrideEventPayload): void => {
  const basePayload = {
    event: 'pricing_override_interaction',
    action: action?.toLowerCase() || undefined,
    detail: detail?.toLowerCase()?.trim()?.slice(0, 75) || undefined,
    section,
    component: component || 'body',
    element_type: elementType,
    element_text: elementText?.toLowerCase() || undefined,
    link_url: linkUrl,
    core_event: 'no',
    user_detail: undefined,
    event_group: undefined,
    event_subgroup: undefined,
    product_id: productId?.toLowerCase() || undefined,
    product_brand: productBrand?.toLowerCase() || undefined,
    product_variant: undefined,
    ...basePayloadOverrides,
  }

  sendEvent({ payload: basePayload })
}
